import { AnimationScenario } from './AnimationScenario';
import { MarkAllAnimationParams } from './MarkAllAnimationParams';
import { AnimationType, MarkAnimationParams } from './MarkAnimationParams';
import { FractionWarperCurve, MarkAnimationTiming } from './MarkAnimationTiming';

// ============================================
// Timing constants
// Mirrors C++ static locals in MarkAllAnimationParamsManager.cpp
// MarkAnimationTiming(delay, duration, warperCurve)
// ============================================

const lin = FractionWarperCurve.Linear;
const eio = FractionWarperCurve.SinusoidalEaseInEaseOut;
const defaultCurve = lin;

const linT0 = new MarkAnimationTiming(0, 1, lin);
const linT1 = new MarkAnimationTiming(1, 1, lin);
const linT2 = new MarkAnimationTiming(2, 1, lin);
const linT3 = new MarkAnimationTiming(3, 1, lin);
const linT4 = new MarkAnimationTiming(4, 1, lin);
const eioT1 = new MarkAnimationTiming(1, 1, eio);
const eioT2 = new MarkAnimationTiming(2, 1, eio);
const eioT3 = new MarkAnimationTiming(3, 1, eio);

// ============================================
// Preset MarkAnimationParams factories
// ============================================

function enterFadeOn2(): MarkAnimationParams {
  return new MarkAnimationParams(linT2, AnimationType.EnterFadeOn);
}
function enterFadeOn3(): MarkAnimationParams {
  return new MarkAnimationParams(linT3, AnimationType.EnterFadeOn);
}
function enterFadeOn4(): MarkAnimationParams {
  return new MarkAnimationParams(linT4, AnimationType.EnterFadeOn);
}
function eioDirectMove1(): MarkAnimationParams {
  return new MarkAnimationParams(eioT1, AnimationType.DirectMove);
}
function linDirectMove1(): MarkAnimationParams {
  return new MarkAnimationParams(linT1, AnimationType.DirectMove);
}
function exitFadeOff0(): MarkAnimationParams {
  return new MarkAnimationParams(linT0, AnimationType.ExitFadeOff);
}
function eioNonMarks1(): MarkAnimationParams {
  return new MarkAnimationParams(eioT1, AnimationType.DirectMove);
}
function linNonMarks1(): MarkAnimationParams {
  return new MarkAnimationParams(linT1, AnimationType.DirectMove);
}
function eioNonMarks2(): MarkAnimationParams {
  return new MarkAnimationParams(eioT2, AnimationType.DirectMove);
}

// ============================================
// Preset MarkAllAnimationParams factories
// Mirrors C++ CreateDefault*Params() methods
// ============================================

function makeTableParams(): MarkAllAnimationParams {
  const move = eioDirectMove1();
  move.setAttributeOverrideTiming('MarkXPosition', eioT2);
  move.setAttributeOverrideTiming('MarkYPosition', eioT3);
  move.setAttributeOverrideTiming('LabelX', eioT2);
  move.setAttributeOverrideTiming('LabelY', eioT3);
  const nonMarks = eioNonMarks2();
  nonMarks.setAttributeOverrideTiming('PaneRectTop', eioT3);
  nonMarks.setAttributeOverrideTiming('PaneRectBottom', eioT3);
  nonMarks.setAttributeOverrideTiming('PaneRectDomainTop', eioT3);
  nonMarks.setAttributeOverrideTiming('PaneRectDomainBottom', eioT3);
  nonMarks.setAttributeOverrideTiming('PaneRectOriginY', eioT3);
  nonMarks.setAttributeOverrideTiming('XRangeMin', eioT3);
  nonMarks.setAttributeOverrideTiming('XRangeMax', eioT3);
  nonMarks.setAttributeOverrideTiming('YRangeMin', eioT3);
  nonMarks.setAttributeOverrideTiming('YRangeMax', eioT3);
  nonMarks.setAttributeOverrideTiming('MapDomainBottom', eioT3);
  nonMarks.setAttributeOverrideTiming('MapDomainTop', eioT3);
  nonMarks.setAttributeOverrideTiming('MapDomainLeft', eioT3);
  nonMarks.setAttributeOverrideTiming('MapDomainRight', eioT3);
  return new MarkAllAnimationParams(enterFadeOn4(), move, exitFadeOff0(), nonMarks, defaultCurve);
}

function makeScatterplotParams(): MarkAllAnimationParams {
  return new MarkAllAnimationParams(enterFadeOn2(), eioDirectMove1(), exitFadeOff0(), eioNonMarks1(), defaultCurve);
}

function makeScatterbarParams(): MarkAllAnimationParams {
  return new MarkAllAnimationParams(enterFadeOn2(), eioDirectMove1(), exitFadeOff0(), eioNonMarks1(), defaultCurve);
}

function makePieChartParams(): MarkAllAnimationParams {
  return new MarkAllAnimationParams(enterFadeOn2(), linDirectMove1(), exitFadeOff0(), linNonMarks1(), defaultCurve);
}

function makeMapParams(): MarkAllAnimationParams {
  return new MarkAllAnimationParams(enterFadeOn2(), eioDirectMove1(), exitFadeOff0(), eioNonMarks1(), defaultCurve);
}

function makeVerticalChartParams(): MarkAllAnimationParams {
  const move = eioDirectMove1();
  move.setAttributeOverrideTiming('MarkXPosition', eioT2);
  move.setAttributeOverrideTiming('MarkSize', eioT2);
  move.setAttributeOverrideTiming('LabelX', eioT2);
  move.setAttributeOverrideTiming('LabelWidth', eioT2);
  const nonMarks = eioNonMarks1();
  nonMarks.setAttributeOverrideTiming('PaneRectLeft', eioT2);
  nonMarks.setAttributeOverrideTiming('PaneRectRight', eioT2);
  nonMarks.setAttributeOverrideTiming('PaneRectDomainLeft', eioT2);
  nonMarks.setAttributeOverrideTiming('PaneRectDomainRight', eioT2);
  nonMarks.setAttributeOverrideTiming('PaneRectOriginX', eioT2);
  return new MarkAllAnimationParams(enterFadeOn3(), move, exitFadeOff0(), nonMarks, defaultCurve);
}

function makeVerticalAreaChartParams(): MarkAllAnimationParams {
  const move = linDirectMove1();
  move.setAttributeOverrideTiming('MarkXPosition', linT2);
  move.setAttributeOverrideTiming('MarkSize', linT2);
  move.setAttributeOverrideTiming('LabelX', eioT2);
  move.setAttributeOverrideTiming('LabelWidth', eioT2);
  const nonMarks = linNonMarks1();
  nonMarks.setAttributeOverrideTiming('PaneRectLeft', linT2);
  nonMarks.setAttributeOverrideTiming('PaneRectRight', linT2);
  nonMarks.setAttributeOverrideTiming('PaneRectDomainLeft', linT2);
  nonMarks.setAttributeOverrideTiming('PaneRectDomainRight', linT2);
  nonMarks.setAttributeOverrideTiming('PaneRectOriginX', linT2);
  return new MarkAllAnimationParams(enterFadeOn3(), move, exitFadeOff0(), nonMarks, defaultCurve);
}

function makeHorizontalChartParams(): MarkAllAnimationParams {
  const move = eioDirectMove1();
  move.setAttributeOverrideTiming('MarkYPosition', eioT2);
  move.setAttributeOverrideTiming('MarkSize', eioT2);
  move.setAttributeOverrideTiming('LabelY', eioT2);
  move.setAttributeOverrideTiming('LabelHeight', eioT2);
  const nonMarks = eioNonMarks1();
  nonMarks.setAttributeOverrideTiming('PaneRectTop', eioT2);
  nonMarks.setAttributeOverrideTiming('PaneRectBottom', eioT2);
  nonMarks.setAttributeOverrideTiming('PaneRectDomainTop', eioT2);
  nonMarks.setAttributeOverrideTiming('PaneRectDomainBottom', eioT2);
  nonMarks.setAttributeOverrideTiming('PaneRectOriginY', eioT2);
  return new MarkAllAnimationParams(enterFadeOn3(), move, exitFadeOff0(), nonMarks, defaultCurve);
}

function makeHorizontalAreaChartParams(): MarkAllAnimationParams {
  const move = linDirectMove1();
  move.setAttributeOverrideTiming('MarkYPosition', linT2);
  move.setAttributeOverrideTiming('MarkSize', linT2);
  move.setAttributeOverrideTiming('LabelY', eioT2);
  move.setAttributeOverrideTiming('LabelHeight', eioT2);
  const nonMarks = linNonMarks1();
  nonMarks.setAttributeOverrideTiming('PaneRectTop', linT2);
  nonMarks.setAttributeOverrideTiming('PaneRectBottom', linT2);
  nonMarks.setAttributeOverrideTiming('PaneRectDomainTop', linT2);
  nonMarks.setAttributeOverrideTiming('PaneRectDomainBottom', linT2);
  nonMarks.setAttributeOverrideTiming('PaneRectOriginY', linT2);
  return new MarkAllAnimationParams(enterFadeOn3(), move, exitFadeOff0(), nonMarks, defaultCurve);
}

function makeCatchallParams(): MarkAllAnimationParams {
  return new MarkAllAnimationParams(enterFadeOn2(), eioDirectMove1(), exitFadeOff0(), eioNonMarks1(), defaultCurve);
}

// ============================================
// Helper used by getFlowAnimationParams
// C++ Reference: anonymous SetTimingDelayAndDuration() in MarkAllAnimationParamsManager.cpp
// ============================================

function setTimingDelayAndDuration(p: MarkAnimationParams, delay: number, duration: number): void {
  p.overallTiming.delay = delay;
  p.overallTiming.duration = duration;
  p.attributeTimingOverrideMap.forEach((timing) => {
    timing.delay = delay;
    timing.duration = duration;
  });
}

// ============================================
// Manager singleton
// ============================================

/**
 * Provides scenario-specific animation parameters.
 * Mirrors C++ MarkAllAnimationParamsManager singleton.
 *
 * C++ Reference: tabanim/main/sceneanimation/MarkAllAnimationParamsManager.cpp
 */
export class MarkAllAnimationParamsManager {
  private static instance: MarkAllAnimationParamsManager | null = null;
  private paramsMap: Map<AnimationScenario, MarkAllAnimationParams>;

  private constructor() {
    this.paramsMap = new Map<AnimationScenario, MarkAllAnimationParams>();
    this.paramsMap.set(AnimationScenario.Table, makeTableParams());
    this.paramsMap.set(AnimationScenario.TableDisabled, makeTableParams());
    this.paramsMap.set(AnimationScenario.Scatterplot, makeScatterplotParams());
    this.paramsMap.set(AnimationScenario.ScatterplotDisabled, makeScatterplotParams());
    this.paramsMap.set(AnimationScenario.MapWithMapMarks, makeMapParams());
    this.paramsMap.set(AnimationScenario.Map, makeMapParams());
    this.paramsMap.set(AnimationScenario.MapDisabled, makeMapParams());
    this.paramsMap.set(AnimationScenario.VerticalAreaChart, makeVerticalAreaChartParams());
    this.paramsMap.set(AnimationScenario.VerticalChart, makeVerticalChartParams());
    this.paramsMap.set(AnimationScenario.VerticalChartDisabled, makeVerticalChartParams());
    this.paramsMap.set(AnimationScenario.HorizontalAreaChart, makeHorizontalAreaChartParams());
    this.paramsMap.set(AnimationScenario.HorizontalChart, makeHorizontalChartParams());
    this.paramsMap.set(AnimationScenario.HorizontalChartDisabled, makeHorizontalChartParams());
    this.paramsMap.set(AnimationScenario.VerticalScatterbar, makeScatterbarParams());
    this.paramsMap.set(AnimationScenario.VerticalScatterbarDisabled, makeScatterbarParams());
    this.paramsMap.set(AnimationScenario.HorizontalScatterbar, makeScatterbarParams());
    this.paramsMap.set(AnimationScenario.HorizontalScatterbarDisabled, makeScatterbarParams());
    this.paramsMap.set(AnimationScenario.PieChart, makePieChartParams());
    this.paramsMap.set(AnimationScenario.Other, makeCatchallParams());
  }

  static getInstance(): MarkAllAnimationParamsManager {
    if (!MarkAllAnimationParamsManager.instance) {
      MarkAllAnimationParamsManager.instance = new MarkAllAnimationParamsManager();
    }
    return MarkAllAnimationParamsManager.instance;
  }

  /**
   * Get scenario-specific params with staggered timings (Sequential / Phased style).
   * C++ Reference: MarkAllAnimationParamsManager::GetAllAnimationParams()
   */
  getAllAnimationParams(scenario: AnimationScenario): MarkAllAnimationParams {
    const params = this.paramsMap.get(scenario) ?? this.paramsMap.get(AnimationScenario.Other);
    return (params ?? makeCatchallParams()).clone();
  }

  /**
   * Get scenario-specific params with all timings flattened to delay=0, duration=1
   * (Simultaneous / Synced style).
   * C++ Reference: MarkAllAnimationParamsManager::GetFlowAnimationParams()
   */
  getFlowAnimationParams(scenario: AnimationScenario): MarkAllAnimationParams {
    const result = this.getAllAnimationParams(scenario);
    setTimingDelayAndDuration(result.enterParams, 0, 1);
    setTimingDelayAndDuration(result.moveParams, 0, 1);
    setTimingDelayAndDuration(result.exitParams, 0, 1);
    setTimingDelayAndDuration(result.nonMarksParams, 0, 1);
    return result;
  }
}
